import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { MAP_TILE_PROVIDER, type MapTileProvider } from "../config/mapTileProvider";
import { formatMapCoordinates, toMapCoordinates } from "../utils/geoCoordinates";

// Leaflet is a sizeable dependency most page loads never touch (below-the-fold, only rendered when
// the result has coordinates) — lazy-loaded into its own chunk instead of shipping on every visit.
const LocationMap = lazy(() => import("./LocationMap").then((module) => ({ default: module.LocationMap })));

export interface LocationMapStrings {
  mapUnavailable: string;
  mapLocationUnavailable: string;
}

interface DeferredLocationMapProps {
  latitude: number | null;
  longitude: number | null;
  label: string;
  strings: LocationMapStrings;
  provider?: MapTileProvider;
}

// The one entry point to the IP location map, shared by the home page and the full result view. The
// map is the last, optional step of the Geo display: the Geo text around it is rendered by the page from
// the backend result and never waits on, or depends on, anything here.
//
// React.lazy starts its import the moment the component renders, which is not the same thing as the
// map being needed: the home page auto-populates the visitor's own result on load, so the map would
// render (and pull Leaflet plus a screenful of tiles from a third-party origin) during the initial page
// load even though it sits far below the fold. Gating the render on an IntersectionObserver keeps all of
// that out of the critical path — and out of LCP — until the user scrolls near it. The placeholder keeps
// its reserved height either way, so nothing shifts when the map does load.
export function DeferredLocationMap({ latitude, longitude, label, strings, provider = MAP_TILE_PROVIDER }: DeferredLocationMapProps) {
  const coordinates = toMapCoordinates(latitude, longitude);

  // No usable point: Leaflet is never loaded, only a one-line state takes the map's place.
  if (!coordinates) {
    return (
      <div className="location-map-status" role="status">
        {strings.mapLocationUnavailable}
      </div>
    );
  }

  // A configured provider that needs a key it wasn't given can only fail — skip Leaflet and every tile
  // request, but keep the coordinates, which don't depend on any basemap.
  if (!provider.enabled) {
    return (
      <div className="location-map-wrap">
        <div className="location-map location-map-status" role="status">
          {strings.mapUnavailable}
        </div>
        <div className="location-map-coords">{formatMapCoordinates(coordinates.latitude, coordinates.longitude)}</div>
      </div>
    );
  }

  return (
    <ViewportGate>
      <Suspense fallback={<div className="location-map-wrap location-map-wrap--loading" />}>
        <LocationMap
          // A new point is a new map: fresh Leaflet instance, fresh tile-failure state.
          key={`${coordinates.latitude},${coordinates.longitude}`}
          latitude={coordinates.latitude}
          longitude={coordinates.longitude}
          label={label}
          unavailableLabel={strings.mapUnavailable}
          provider={provider}
        />
      </Suspense>
    </ViewportGate>
  );
}

function ViewportGate({ children }: { children: ReactNode }) {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const el = placeholderRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShouldRender(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!shouldRender) return <div ref={placeholderRef} className="location-map-wrap location-map-wrap--loading" />;
  return <>{children}</>;
}
